"use client";
import { useEffect } from "react";
import Cookies from "js-cookie";
import { DateTime } from "luxon";
import {
  GA4_TRACKING_COOKIE,
  normalizeGaClientId,
  isGaAnalyticsDenied,
} from "@portal/app/_common/lib/ga4Tracking";

/** Read IDs from the configured Google tag, rather than parsing versioned GA session cookies. */
export function Ga4SessionCaptureScript() {
  useEffect(() => {
    const measurementId = process.env.GOOGLE_ANALYTICS_MEASUREMENT_ID;
    if (!measurementId) return;
    let analyticsDenied = isGaAnalyticsDenied(Cookies.get("cookieyes-consent"));
    if (analyticsDenied) Cookies.remove(GA4_TRACKING_COOKIE, { path: "/" });
    const onConsent = (event: Event) => {
      const detail = (event as CustomEvent<{ accepted?: string[]; rejected?: string[] }>).detail;
      if (detail?.rejected?.includes("analytics")) {
        analyticsDenied = true;
        Cookies.remove(GA4_TRACKING_COOKIE, { path: "/" });
      } else if (detail?.accepted?.includes("analytics")) analyticsDenied = false;
    };
    document.addEventListener("cookieyes_consent_update", onConsent);
    let disposed = false;
    let pending = false;
    const capture = () => {
      if (pending || disposed || analyticsDenied || !Cookies.get("_ga")) return;
      const target = window as unknown as {
        gtag?: (...args: unknown[]) => void;
        dataLayer?: unknown[];
      };
      // GTM can install the Google tag without defining gtag. Queue only get commands,
      // using Google's arguments dispatcher; do not configure/load another tag.
      if (!target.gtag) {
        target.dataLayer ??= [];
        target.gtag = function () {
          target.dataLayer!.push(arguments);
        };
      }
      const gtag = target.gtag;
      pending = true;
      const timeout = window.setTimeout(() => {
        pending = false;
      }, 2000);
      gtag("get", measurementId, "client_id", (rawClientId: unknown) => {
        const gaClientId = normalizeGaClientId(rawClientId);
        if (!gaClientId || disposed) return;
        gtag("get", measurementId, "session_id", (rawSessionId: unknown) => {
          if (disposed || analyticsDenied || !/^\d{1,16}$/.test(String(rawSessionId))) return;
          Cookies.set(
            GA4_TRACKING_COOKIE,
            JSON.stringify({
              gaClientId,
              gaSessionId: String(rawSessionId),
              gaSessionCapturedAt: DateTime.utc().toISO(),
            }),
            { expires: 1 / 24, path: "/", sameSite: "lax", secure: location.protocol === "https:" }
          );
          window.clearTimeout(timeout);
          pending = false;
        });
      });
    };
    capture();
    const timer = window.setInterval(capture, 10000);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      document.removeEventListener("cookieyes_consent_update", onConsent);
    };
  }, []);
  return null;
}
